import type { History } from 'history';
import { Fragment } from 'react';
import { useHistory } from 'react-router-dom';
import '../../styles.css';
import { t } from '@avalara/bat-apps-shared/i18n';
import type { Connector } from '@avalara/types/index';
import SelectButton from './select-button';

type Props = {
  myConnectors: Array<Connector>;
  apiConnector?: Connector;
  inIntegrations: boolean;
  inArchetypeApp?: boolean;
  isEligibleToBuy: boolean;
  isFreeTrialCustomer?: boolean;
  isMobile: boolean;
  onShowConnectorDetails: (arg0: Connector) => void;
  onSelectConnector: (arg0: History<unknown> | null | undefined) => void;
  onTrackClickedLink: (arg0: string) => void;
  selectedConnector: Connector | null;
  toggleFooter?: () => void;
  fallbackImg: string;
  showOtherDetails: boolean;
  avataxLink: string;
};

const ConnectorInfo = ({
  myConnectors,
  apiConnector,
  inIntegrations,
  isEligibleToBuy,
  isFreeTrialCustomer,
  onShowConnectorDetails,
  onSelectConnector,
  onTrackClickedLink,
  selectedConnector,
  toggleFooter,
  showOtherDetails,
  avataxLink,
}: Props) => {
  const history = useHistory();
  const displayBadges = selectedConnector?.versions?.[0]?.badges.filter((b) => b.status === 'Certified' && b.docLink);
  const selectedConnectorInfo = selectedConnector && (
    <div className='connector-information-container'>
      <div className={`${inIntegrations ? 'margin-top-lg' : ''} select-container`}>
        {/* NOTE(Jeremy): These are ordered in YODA */}
        {displayBadges && displayBadges.length > 0 ? (
          <Fragment>
            {/* <h3 className='title'>{selectedConnector.name}</h3> */}
            <p className='title-certified'>{t('shared.general.certified-by-avalara-for-the-following')}:</p>
            <div className='badge-wrapper'>
              {displayBadges.map((b) => (
                <div className='badge' key={b.id}>
                  <a href={b.docLink} target='_blank' rel='noopener noreferrer'>
                    <img src='https://assets.avalara.com/skylab-sdk/2.16.0/images/app-icon-32x32.png' alt={b.id} />
                    <span
                      className={`${inIntegrations && b.description != null ? 'integrations-tooltip' : ''} ${b.description != null ? 'tooltip' : ''}`}
                    >
                      {b.description}
                    </span>
                    <div>{b.name != null ? b.name : b.id}</div>
                  </a>
                </div>
              ))}
            </div>
          </Fragment>
        ) : null}
        <SelectButton
          myConnectors={myConnectors}
          history={history}
          inIntegrations={inIntegrations}
          isEligibleToBuy={isEligibleToBuy}
          isFreeTrialCustomer={isFreeTrialCustomer}
          onSelectConnector={onSelectConnector}
          selectedConnector={selectedConnector}
          toggleFooter={toggleFooter}
        />
      </div>
    </div>
  );

  const paidForUser = inIntegrations && !isFreeTrialCustomer;
  const newUser = !paidForUser && !inIntegrations;
  const freeTrialUser = !paidForUser && inIntegrations;
  const contactUsText = 'Contact us to discuss an integration partnership';

  // NOTE(Jeremy): inline styling to override css file (main.css)
  const BuildIntegration = apiConnector && (
    <tr style={{ border: 'none' }}>
      <td>
        <img className='small' src={apiConnector.logo} alt='API' />
      </td>
      <td>
        <button
          type='button'
          id='build-it'
          className='tertiary'
          data-ref='buy-account-shared.connector-store.select-custom-integration'
          onClick={
            paidForUser
              ? toggleFooter
              : () => {
                  onShowConnectorDetails(apiConnector);
                  onTrackClickedLink('CONNECTOR_STORE_CUSTOM_INTEGRATION');
                }
          }
        >
          {"Build your own integration with AvaTax API's"}
        </button>
      </td>
    </tr>
  );

  const CalcDash = (
    <tr>
      <td>
        <s-icon className='icon download' name='download' />
      </td>
      <td>
        <button
          type='button'
          id='new-user'
          className='tertiary'
          data-ref='buy-account-shared.connector-store.select-import-transactions'
          onClick={() => onTrackClickedLink('CONNECTOR_STORE_CALC_DASH')}
        >
          <a id='calc-dash' className='tertiary' href={avataxLink}>
            {t('shared.buttons.import-transactions-to-avatax')}
          </a>
        </button>
      </td>
    </tr>
  );

  const PartnerWithUsNewUser = (
    <tr>
      <td>
        <img
          className='small'
          alt={t('shared.accessibility.cam-account-distribution')}
          src='https://s3-us-west-2.amazonaws.com/avalara-biztech-caching-fqa/customer/cam_photos/005M'
        />
      </td>
      <td>
        <button
          type='button'
          id='new-user'
          className='tertiary'
          data-ref='buy-account-shared.connector-store.select-parnter-new-user'
          onClick={() => onTrackClickedLink('CONNECTOR_STORE_NEW_USER_PARTNER')}
        >
          <a className='tertiary' href='tel:+18772243650'>
            {contactUsText}
          </a>
        </button>
      </td>
    </tr>
  );

  const PartnerWithUsExistingUser = (
    <tr>
      <td>
        <img
          className='small'
          alt={t('shared.accessibility.cam-account-distribution')}
          src='https://s3-us-west-2.amazonaws.com/avalara-biztech-caching-fqa/customer/cam_photos/005M'
        />
      </td>
      <td>
        <button type='button' id='free-trial-user-link' className='tertiary' onClick={toggleFooter}>
          {contactUsText}
        </button>
      </td>
    </tr>
  );

  const connectorNotFound = (
    <div className='connector-information-container'>
      <div className={`${inIntegrations ? 'margin-top-lg' : ''} select-container`}>
        <h3 className='spaced-header'>
          {"Looks like you can't find the integration you are looking for. Here are available options:"}
        </h3>
        <table>
          <tbody>
            {BuildIntegration}
            {!newUser && CalcDash}
            {newUser && PartnerWithUsNewUser}
            {freeTrialUser && PartnerWithUsExistingUser}
          </tbody>
        </table>
      </div>
    </div>
  );

  if (selectedConnector) {
    return selectedConnectorInfo;
  }
  if (showOtherDetails) {
    return connectorNotFound;
  }
  return null;
};

export default ConnectorInfo;
